import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Page from '../container/Page';
import ProductHeroHeader from './ProductHeroHeader';
import NutritionLabel from '@/components/NutritionLabel';
import useCatalog from '@/hooks/useCatalog';
import LoadingSpinner from '@/components/Loaders/PageLoader/loadingSpinner';
import CustomerReviews from '@/components/CustomerReviews/CustomerReviews';
import cntl from 'cntl';

// Product.tsx Refinement Snippet
const transparencySectionCn = cntl`
relative
  w-full 
  max-w-7xl 
  mx-auto 
  my-16 
  px-4 
  grid 
  grid-cols-1 
  md:flex
  gap-12 
  items-start
  md:justify-center
`;

const stickyColCn = cntl`
  col-span-1 
  lg:sticky 
  lg:top-24 
  flex 
  flex-col 
  items-center
`;

const reviewsColCn = cntl`
  lg:col-span-8 
  w-full
`;

export default () => {
  const { id } = useParams<{ id: string }>();
  const { item, isReady } = useCatalog(id);

  // Helper to map robust data if needed
  const renderNutrition = item?.nutritionFacts && (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.6 }}
      className="flex flex-col items-center w-full max-w-lg mx-auto my-12"
    >
      <div className="mb-8 text-center">
        <h2 className="font-display text-2xl text-text-heading font-bold tracking-wide">
          Nutritional Transparency
        </h2>
        <p className="font-body text-text-muted mt-2 text-sm">
          Everything you need to know about what's inside.
        </p>
      </div>

      {/* Container providing a modern lift to the standard label */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 p-4 rounded-xl shadow-lift border border-brand-frost/40">
        <NutritionLabel
          backgroundColor="white"
          servingSize={`${item.nutritionFacts.servingSize.weight} - ${item?.nutritionFacts?.servingSize?.weight + 0.5}oz`}
          servingsPerContainer={item.nutritionFacts.servingSize.amount}
          calories={item.nutritionFacts.calories}
          // Defaulting macro math for from-fat calories
          caloriesFromFat={Math.round(item.nutritionFacts.totalFat * 9)}
          totalFat={item.nutritionFacts.totalFat}
          saturatedFat={item.nutritionFacts.saturatedFat}
          transFat={item.nutritionFacts.transFat}
          cholesterol={item.nutritionFacts.cholesterol}
          // Sodium is not strictly in the provided type snippet, defaulting to 0
          sodium={0}
          totalCarbohydrate={item.nutritionFacts.totalCarbohydrates}
          dietaryFiber={item.nutritionFacts.dietaryFiber}
          sugars={item.nutritionFacts.totalSugars}
          protein={item.nutritionFacts.protein}
          vitamins={[]}
        />
      </div>
    </motion.div>
  );

  // Inside your Product component render:
  const renderTransparency = item?.nutritionFacts && (
    <section className={transparencySectionCn}>
      {/* Left Column: Nutritional Facts (Sticky) */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        className={stickyColCn}
      >
        <div className="mb-6 text-center lg:text-left w-full">
          <h2 className="font-display text-2xl text-text-heading font-bold">
            Clean Ingredients
          </h2>
          <p className="font-body text-text-muted text-sm italic">
            Transparent sourcing for every bite.
          </p>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-xl border border-brand-truffle/5">
          <NutritionLabel
            servingSize={`${item.nutritionFacts.servingSize.weight} - ${item?.nutritionFacts?.servingSize?.weight + 0.5}oz`}
            servingsPerContainer={item.nutritionFacts.servingSize.amount}
            calories={item.nutritionFacts.calories}
            totalFat={item.nutritionFacts.totalFat}
            saturatedFat={item.nutritionFacts.saturatedFat}
            transFat={item.nutritionFacts.transFat}
            cholesterol={item.nutritionFacts.cholesterol}
            sodium={0}
            totalCarbohydrate={item.nutritionFacts.totalCarbohydrates}
            dietaryFiber={item.nutritionFacts.dietaryFiber}
            sugars={item.nutritionFacts.totalSugars}
            protein={item.nutritionFacts.protein}
            vitamins={[]}
          />
        </div>
      </motion.div>

      {/* Right Column: Customer Reviews (Scrollable) */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        className={reviewsColCn}
      >
        <CustomerReviews
          product={item} // Pass the GID to the component
        />
      </motion.div>
    </section>
  );

  return (
    <Page
      variant="frost"
      // Inject the Hero Header into the Page framework
      pageHeader={<ProductHeroHeader product={item} loading={!isReady} />}
    >
      {/* Loading overlay for the duration of the API fetch */}
      {!isReady && (
        <LoadingSpinner global message="Preparing product details..." />
      )}

      {/* Primary child content handles the dynamically mapped Nutrition Label */}
      {isReady && item && (
        <div className="w-full flex-1 flex flex-col items-center animate-in fade-in duration-500">
          {renderTransparency}
        </div>
      )}
    </Page>
  );
};
